/**
 * Audio, speech and haptic cues for apnea sessions.
 *
 * The session is run lying down with eyes closed, so audio is the primary
 * interface, not a decoration. Cues must be distinguishable by pitch and pattern
 * without looking at the screen.
 *
 * Tones are synthesised with the Web Audio API rather than shipped as files: no
 * network dependency mid-session, no decode latency, and exact control over
 * pitch so the cues stay mutually distinguishable.
 *
 * Everything here degrades silently. A browser without Web Audio, speech
 * synthesis or vibration must still give a fully usable session.
 */

export type ApneaCue =
  | 'sessionStart'
  | 'breatheIn'
  | 'breatheOut'
  | 'holdStart'
  | 'holdWarning'
  | 'holdTarget'
  | 'holdEnd'
  | 'recoveryBreath'
  | 'monitorStart'
  | 'setComplete'
  | 'sessionComplete'
  | 'warning'
  | 'holdCheckpoint';

interface ToneSpec {
  /** Frequency in Hz, or a sequence for a multi-note cue. */
  notes: number[];
  /** Duration of each note, in milliseconds. */
  noteMs: number;
  /** Gap between notes, in milliseconds. */
  gapMs?: number;
}

/**
 * Cue design: rising pitch = something starting, falling = something ending,
 * repeated notes = a count. Kept within 400-900 Hz, which stays audible on a
 * phone speaker lying on the floor without being shrill.
 */
const TONES: Record<ApneaCue, ToneSpec> = {
  sessionStart: { notes: [523, 659], noteMs: 140, gapMs: 60 },
  breatheIn: { notes: [660], noteMs: 90 },
  breatheOut: { notes: [440], noteMs: 90 },
  // Distinctly low and long: the most important transition to recognise.
  holdStart: { notes: [392], noteMs: 320 },
  // Two short low notes: target is close, not reached — kept calm, not alarming.
  holdWarning: { notes: [440, 440], noteMs: 130, gapMs: 90 },
  // A single mid chime: target reached, no urgency implied.
  holdTarget: { notes: [587], noteMs: 180 },
  // Rising pair: breathe now.
  holdEnd: { notes: [523, 784], noteMs: 150, gapMs: 40 },
  recoveryBreath: { notes: [880], noteMs: 70 },
  monitorStart: { notes: [494], noteMs: 200 },
  setComplete: { notes: [659, 523], noteMs: 130, gapMs: 50 },
  sessionComplete: { notes: [523, 659, 784], noteMs: 180, gapMs: 60 },
  // Deliberately dissonant, so it is never mistaken for a progress cue.
  warning: { notes: [370, 370, 370], noteMs: 160, gapMs: 90 },
  // A single soft tick — just a time marker on an open-ended hold, not a transition.
  holdCheckpoint: { notes: [494], noteMs: 90 },
};

const VIBRATION: Partial<Record<ApneaCue, number | number[]>> = {
  holdStart: 400,
  holdWarning: [100, 70, 100],
  holdTarget: 150,
  holdEnd: [120, 80, 120],
  recoveryBreath: 60,
  monitorStart: 200,
  setComplete: [100, 60, 100],
  sessionComplete: [150, 80, 150, 80, 150],
  warning: [200, 100, 200, 100, 200],
  holdCheckpoint: 50,
};

export interface ApneaAudioOptions {
  tones?: boolean;
  speech?: boolean;
  vibration?: boolean;
}

export class ApneaAudio {
  private ctx: AudioContext | null = null;
  private options: Required<ApneaAudioOptions>;

  constructor(options: ApneaAudioOptions = {}) {
    this.options = {
      tones: options.tones ?? true,
      speech: options.speech ?? false,
      vibration: options.vibration ?? true,
    };
  }

  setOptions(options: ApneaAudioOptions): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Must be called from a user gesture. Browsers start an AudioContext
   * suspended, so without this the first several cues would be silent — during
   * a session the user cannot see, which is exactly when that matters most.
   */
  async prime(): Promise<void> {
    if (!this.options.tones) return;
    try {
      const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx ??= new Ctor();
      if (this.ctx.state === 'suspended') await this.ctx.resume();
    } catch {
      // No audio available; the session stays usable visually.
      this.ctx = null;
    }
  }

  play(cue: ApneaCue): void {
    this.playTone(cue);
    this.vibrate(cue);
  }

  private playTone(cue: ApneaCue): void {
    if (!this.options.tones || !this.ctx || this.ctx.state !== 'running') return;
    const spec = TONES[cue];
    if (!spec) return;

    try {
      const gap = spec.gapMs ?? 0;
      spec.notes.forEach((freq, i) => {
        const startAt = this.ctx!.currentTime + (i * (spec.noteMs + gap)) / 1000;
        const osc = this.ctx!.createOscillator();
        const gain = this.ctx!.createGain();
        osc.frequency.value = freq;
        osc.type = 'sine';

        // Short attack/release ramps; a hard gate produces an audible click.
        const dur = spec.noteMs / 1000;
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(0.28, startAt + 0.012);
        gain.gain.setValueAtTime(0.28, startAt + dur - 0.03);
        gain.gain.linearRampToValueAtTime(0, startAt + dur);

        osc.connect(gain).connect(this.ctx!.destination);
        osc.start(startAt);
        osc.stop(startAt + dur + 0.02);
      });
    } catch {
      // Ignore: a failed cue must never interrupt a session.
    }
  }

  private vibrate(cue: ApneaCue): void {
    if (!this.options.vibration) return;
    const pattern = VIBRATION[cue];
    if (pattern === undefined) return;
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // Unsupported; ignore.
    }
  }

  /**
   * Spoken announcement. Cancels any in-flight utterance so a queue cannot build
   * up while the tab is backgrounded and then fire all at once on return.
   */
  speak(text: string): void {
    if (!this.options.speech) return;
    try {
      if (!('speechSynthesis' in window)) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.volume = 0.9;
      window.speechSynthesis.speak(utterance);
    } catch {
      // Unsupported; ignore.
    }
  }

  dispose(): void {
    try {
      window.speechSynthesis?.cancel();
      this.ctx?.close();
    } catch {
      // Ignore.
    }
    this.ctx = null;
  }
}

/** Spoken text per cue. Deliberately terse — heard, not read. */
export const cueSpeech = (
  cue: ApneaCue,
  context?: { setNumber?: number; totalSets?: number; holdSeconds?: number }
): string | null => {
  switch (cue) {
    case 'holdStart':
      return context?.setNumber
        ? `Set ${context.setNumber}. Hold.`
        : 'Hold';
    case 'holdWarning':
      return context?.holdSeconds
        ? `${formatCountdownSpeech(context.holdSeconds)} to target`
        : 'Approaching target';
    case 'holdTarget': return 'Target reached';
    case 'holdEnd': return 'Breathe';
    case 'monitorStart': return 'Stay upright. Keep breathing.';
    case 'setComplete':
      return context?.setNumber && context?.totalSets
        ? `Set ${context.setNumber} of ${context.totalSets} complete`
        : 'Set complete';
    case 'sessionComplete': return 'Session complete';
    case 'warning': return 'Stop and breathe';
    case 'holdCheckpoint':
      return context?.holdSeconds ? formatCountdownSpeech(context.holdSeconds) : null;
    default: return null;
  }
};

export const formatCountdownSpeech = (seconds: number): string =>
  seconds >= 60
    ? `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) === 1 ? '' : 's'}`
    : `${seconds} seconds`;
